import { BinaryType } from "../@types/Binary";
import { Arquitecture } from "./Arquitecture";
import * as fs from "fs";
import * as path from "path";
import * as os from "os";
import followRedirects from 'follow-redirects';
const { https } = followRedirects;
import * as unzipper from "unzipper";
import { IncomingMessage } from 'http';
import ora from "ora";
import chalk from "chalk";
import { fileURLToPath } from "url";
import { dirname } from "path";

interface DownloadProgress {
    downloaded: number;
    total: number;
    percentage: number;
}

interface BinaryVersion {
    date_published: string;
    name: string;
    version: string;
    platforms: string[];
    download_url: string;
}

class Downloader {
    private static mainSpinner: any = null;
    private static currentSpinner: any = null;
    private static readonly VERSION_URLS = {
        query: 'https://raw.githubusercontent.com/Dbcube/binaries/main/query-engines.json',
        schema: 'https://raw.githubusercontent.com/Dbcube/binaries/main/schema-engines.json',
        sqlite: 'https://raw.githubusercontent.com/Dbcube/binaries/main/sqlite-engines.json'
    };

    /**
     * Fetch latest version from GitHub
     */
    private static async fetchLatestVersion(prefix: 'query' | 'schema' | 'sqlite'): Promise<string> {
        const url = this.VERSION_URLS[prefix];

        return new Promise((resolve, reject) => {
            https.get(url, (response: IncomingMessage) => {
                let data = '';

                response.on('data', (chunk) => {
                    data += chunk;
                });

                response.on('end', () => {
                    try {
                        const versions: BinaryVersion[] = JSON.parse(data);
                        if (versions && versions.length > 0) {
                            // Get the latest version (first in array)
                            resolve(versions[0].version);
                        } else {
                            reject(new Error('No versions found'));
                        }
                    } catch (error) {
                        reject(error);
                    }
                });

                response.on('error', reject);
            }).on('error', reject);
        });
    }

    /**
     * Extract version from binary filename
     * Example: schema-engine-v1.0.0-windows-x64.exe -> v1.0.0
     */
    private static extractVersionFromFilename(filename: string): string | null {
        const match = filename.match(/-(v\d+\.\d+\.\d+)-/);
        return match ? match[1] : null;
    }

    /**
     * Get local version of installed binary
     */
    private static getLocalVersion(binDir: string, prefix: string): string | null {
        try {
            const files = fs.readdirSync(binDir);
            const binaryPattern = new RegExp(`^${prefix}-engine-v`);
            const binaryFile = files.find(f => binaryPattern.test(f));

            if (binaryFile) {
                return this.extractVersionFromFilename(binaryFile);
            }
        } catch (error) {
            // Directory doesn't exist or can't read
        }
        return null;
    }

    /**
     * Compare versions (returns true if remote is newer)
     */
    private static isNewerVersion(localVersion: string | null, remoteVersion: string): boolean {
        if (!localVersion) return true; // No local version, download

        // Remove 'v' prefix if exists
        const cleanLocal = localVersion.replace(/^v/, '');
        const cleanRemote = remoteVersion.replace(/^v/, '');

        const localParts = cleanLocal.split('.').map(Number);
        const remoteParts = cleanRemote.split('.').map(Number);

        for (let i = 0; i < 3; i++) {
            if (remoteParts[i] > localParts[i]) return true;
            if (remoteParts[i] < localParts[i]) return false;
        }

        return false; // Versions are equal
    }

    /**
     * Delete old binary files
     */
    private static deleteOldBinary(binDir: string, prefix: string): void {
        try {
            const files = fs.readdirSync(binDir);
            const binaryPattern = new RegExp(`^${prefix}-engine-`);

            files.forEach(file => {
                if (binaryPattern.test(file)) {
                    const filePath = path.join(binDir, file);
                    try {
                        fs.unlinkSync(filePath);
                        console.log(`🗑️  Deleted old binary: ${file}`);
                    } catch (err) {
                        console.warn(`⚠️  Could not delete: ${file}`);
                    }
                }
            });
        } catch (error) {
            // Ignore errors
        }
    }

    static get(prefix: string): BinaryType & { name: string; url: string; version: string } {
        const arch = new Arquitecture();
        const platform = arch.getPlatform();
        const architecture = arch.getArchitecture();

        const platformMap: Record<string, string> = {
            windows: "windows",
            linux: "linux",
            darwin: "macos"
        };

        const archMap: Record<string, string> = {
            x86_64: "x64",
            aarch64: "arm64"
        };

        const plat = platformMap[platform];
        const archSuffix = archMap[architecture];

        if (plat && archSuffix) {
            // Note: This is a sync method, version will be "latest" placeholder
            // The actual version will be resolved during download
            const baseName = `${prefix}-engine-${plat}-${archSuffix}`;
            const binaryName = platform === "windows" ? `${baseName}.exe` : baseName;

            const url = `https://github.com/Dbcube/binaries/releases/download/${prefix}-engine/${prefix}-engine-latest-${plat}-${archSuffix}.zip`;
            return {
                name: binaryName,
                url,
                version: "latest",
                query_engine: binaryName,
                schema_engine: `${prefix}-engine-${plat}-${archSuffix}${platform === "windows" ? ".exe" : ""}`
            };
        }

        return {
            name: "",
            url: "",
            version: "",
            query_engine: "",
            schema_engine: ""
        };
    }

    /**
     * Get binary info with actual version from GitHub
     */
    static async getWithVersion(prefix: 'query' | 'schema' | 'sqlite'): Promise<BinaryType & { name: string; url: string; version: string }> {
        const arch = new Arquitecture();
        const platform = arch.getPlatform();
        const architecture = arch.getArchitecture();

        const platformMap: Record<string, string> = {
            windows: "windows",
            linux: "linux",
            darwin: "macos"
        };

        const archMap: Record<string, string> = {
            x86_64: "x64",
            aarch64: "arm64"
        };

        const plat = platformMap[platform];
        const archSuffix = archMap[architecture];

        if (plat && archSuffix) {
            // Fetch latest version from GitHub
            const version = await this.fetchLatestVersion(prefix);

            const baseName = `${prefix}-engine-${version}-${plat}-${archSuffix}`;
            const binaryName = platform === "windows" ? `${baseName}.exe` : baseName;

            const url = `https://github.com/Dbcube/binaries/releases/download/${prefix}-engine/${prefix}-engine-${version}-${plat}-${archSuffix}.zip`;
            return {
                name: binaryName,
                url,
                version,
                query_engine: binaryName,
                schema_engine: binaryName
            };
        }

        return {
            name: "",
            url: "",
            version: "",
            query_engine: "",
            schema_engine: ""
        };
    }

    static async download(targetDir?: string): Promise<void> {
        const binDir = targetDir || this.getDefaultBinDir();
        fs.mkdirSync(binDir, { recursive: true });

        // Initialize main spinner
        this.mainSpinner = ora({
            text: chalk.blue('Verificando versiones de binarios...'),
            spinner: 'dots12'
        }).start();

        const binariesToProcess: Array<{ prefix: 'query' | 'schema' | 'sqlite'; needsUpdate: boolean; localVersion: string | null; remoteVersion: string }> = [];

        // Check versions for all engines
        for (const prefix of ['query', 'schema', 'sqlite'] as const) {
            try {
                const localVersion = this.getLocalVersion(binDir, prefix);
                const remoteVersion = await this.fetchLatestVersion(prefix);
                const needsUpdate = this.isNewerVersion(localVersion, remoteVersion);

                binariesToProcess.push({
                    prefix,
                    needsUpdate,
                    localVersion,
                    remoteVersion
                });

                if (needsUpdate) {
                    console.log(`\n📦 ${prefix}-engine: ${localVersion || 'not installed'} → ${remoteVersion}`);
                } else if (localVersion) {
                    console.log(`\n✅ ${prefix}-engine: ${localVersion} (up to date)`);
                }
            } catch (error) {
                console.warn(`⚠️  Could not check version for ${prefix}-engine, will attempt download`);
                binariesToProcess.push({
                    prefix,
                    needsUpdate: true,
                    localVersion: null,
                    remoteVersion: 'latest'
                });
            }
        }

        const binariesToDownload = binariesToProcess.filter(b => b.needsUpdate);

        if (binariesToDownload.length === 0) {
            this.mainSpinner.succeed(chalk.green('All binaries are up to date'));
            return;
        }

        // Show update progress
        this.mainSpinner.text = chalk.blue(`Updating ${binariesToDownload.length} binary(ies)...`);

        try {
            // Download and update binaries in parallel
            await Promise.all(binariesToDownload.map(async (binary) => {
                const maxRetries = 3;
                let attempt = 0;

                while (attempt <= maxRetries) {
                    try {
                        // Get binary info with version
                        const binaryInfo = await this.getWithVersion(binary.prefix);

                        if (!binaryInfo.name || !binaryInfo.url) {
                            throw new Error(`Platform or architecture not supported for ${binary.prefix}`);
                        }

                        const tempZipPath = path.join(os.tmpdir(), `dbcube-${binary.prefix}-${Date.now()}.zip`);
                        const finalBinaryPath = path.join(binDir, binaryInfo.name);

                        // Delete old binary first
                        this.deleteOldBinary(binDir, binary.prefix);

                        // Download
                        await this.downloadFileWithProgress(binaryInfo.url, tempZipPath, binary.prefix);

                        // Extract
                        await this.extractBinary(tempZipPath, finalBinaryPath, binary.prefix);

                        console.log(`✅ ${binary.prefix}-engine updated to ${binary.remoteVersion}`);
                        break;

                    } catch (error: unknown) {
                        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

                        if (attempt < maxRetries && (
                            errorMessage.includes('ECONNRESET') ||
                            errorMessage.includes('timeout') ||
                            errorMessage.includes('ETIMEDOUT') ||
                            errorMessage.includes('ENOTFOUND')
                        )) {
                            attempt++;
                            console.log(`🔄 Retrying ${binary.prefix}-engine (${attempt}/${maxRetries})...`);
                            await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
                        } else {
                            throw new Error(`Error downloading ${binary.prefix}: ${errorMessage}`);
                        }
                    }
                }
            }));

            // Complete successfully
            this.mainSpinner.succeed(chalk.green('Binaries updated successfully'));
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            this.mainSpinner.fail(chalk.red(`Error updating binaries: ${errorMessage}`));
            throw error;
        }
    }

    private static updateMainProgress(binary: string, current: number, total: number, status: string, attempt?: number) {
        const progressBar = this.createProgressBar(current, total);
        const statusEmojis = {
            downloading: '📥',
            extracting: '📦',
            completed: '✅',
            exists: '✅',
            retrying: '🔄'
        };

        const statusMessages = {
            downloading: chalk.blue('descargando'),
            extracting: chalk.yellow('extrayendo'),
            completed: chalk.green('completado'),
            exists: chalk.gray('existe'),
            retrying: chalk.yellow(`reintentando (${attempt}/${3})`)
        };

        const emoji = statusEmojis[status as keyof typeof statusEmojis] || '📥';
        const message = statusMessages[status as keyof typeof statusMessages] || status;

        this.mainSpinner.text = `${progressBar} ${emoji} ${chalk.bold(binary)} - ${message}`;
    }

    private static createProgressBar(current: number, total: number, width: number = 20): string {
        const filled = Math.round((current / total) * width);
        const empty = width - filled;
        const filledBar = chalk.green('█'.repeat(filled));
        const emptyBar = chalk.gray('░'.repeat(empty));
        const percentage = chalk.bold(`${current}/${total}`);
        return `[${filledBar}${emptyBar}] ${percentage}`;
    }

    private static downloadFileWithProgress(url: string, outputPath: string, prefix: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = https.get(url, { timeout: 0 }, (response: IncomingMessage) => {
                if (response.statusCode === 302 || response.statusCode === 301) {
                    const redirectUrl = response.headers.location;
                    if (redirectUrl) {
                        return this.downloadFileWithProgress(redirectUrl, outputPath, prefix).then(resolve).catch(reject);
                    }
                }

                if (response.statusCode !== 200) {
                    reject(new Error(`HTTP ${response.statusCode} para ${prefix}`));
                    return;
                }

                const file = fs.createWriteStream(outputPath);
                const totalBytes = parseInt(response.headers['content-length'] || '0', 10);
                let downloadedBytes = 0;

                response.on('data', (chunk) => {
                    downloadedBytes += chunk.length;
                    file.write(chunk);

                    if (totalBytes > 0) {
                        const progress: DownloadProgress = {
                            downloaded: downloadedBytes,
                            total: totalBytes,
                            percentage: (downloadedBytes / totalBytes) * 100
                        };
                        this.updateDownloadProgress(prefix, progress);
                    }
                });

                response.on('end', () => {
                    file.end();
                    resolve();
                });

                response.on('error', (err) => {
                    file.close();
                    this.cleanupFile(outputPath);
                    reject(err);
                });

                file.on('error', (err) => {
                    file.close();
                    this.cleanupFile(outputPath);
                    reject(err);
                });
            });

            request.on('error', reject);
            request.on('timeout', () => {
                request.destroy();
                reject(new Error(`Timeout descargando ${prefix}`));
            });
        });
    }

    private static updateDownloadProgress(binary: string, progress: DownloadProgress) {
        const percentage = progress.percentage.toFixed(1);
        const downloaded = (progress.downloaded / 1024 / 1024).toFixed(1);
        const total = (progress.total / 1024 / 1024).toFixed(1);

        // Create a mini progress bar for the binary
        const barWidth = 15;
        const filled = Math.round((progress.percentage / 100) * barWidth);
        const empty = barWidth - filled;
        const progressBar = chalk.green('█'.repeat(filled)) + chalk.gray('░'.repeat(empty));

        const progressText = `[${progressBar}] ${percentage}% (${downloaded}/${total}MB)`;
        this.mainSpinner.text = `📥 ${chalk.bold(binary)} - ${progressText}`;
    }

    private static extractBinary(zipPath: string, outputPath: string, prefix: string): Promise<void> {
        return new Promise((resolve, reject) => {
            let extracted = false;

            fs.createReadStream(zipPath)
                .pipe(unzipper.Parse())
                .on('entry', (entry: unzipper.Entry) => {
                    if (entry.type === 'File' && !extracted) {
                        extracted = true;
                        const writeStream = fs.createWriteStream(outputPath);

                        entry.pipe(writeStream);

                        writeStream.on('finish', () => {
                            if (process.platform !== 'win32') {
                                fs.chmodSync(outputPath, 0o755);
                            }
                            this.cleanupFile(zipPath);
                            resolve();
                        });

                        writeStream.on('error', (err: Error) => {
                            this.cleanupFile(zipPath);
                            reject(err);
                        });
                    } else {
                        entry.autodrain();
                    }
                })
                .on('error', (err: Error) => {
                    this.cleanupFile(zipPath);
                    reject(err);
                })
                .on('close', () => {
                    if (!extracted) {
                        this.cleanupFile(zipPath);
                        reject(new Error(`No se encontró archivo válido en el ZIP para ${prefix}`));
                    }
                });
        });
    }

    private static cleanupFile(filePath: string): void {
        try {
            if (fs.existsSync(filePath)) {
                fs.unlinkSync(filePath);
            }
        } catch {
            // Ignore cleanup errors
        }
    }

    private static getDefaultBinDir(): string {
        // Get __dirname equivalent for ESM
        const __filename = typeof import.meta !== 'undefined' && import.meta.url
            ? fileURLToPath(import.meta.url)
            : '';
        const __dirname = __filename ? dirname(__filename) : process.cwd();

        // Try to find a suitable directory for binaries in order of preference
        const possibleDirs = [
            path.resolve(process.cwd(), '.dbcube', 'bin'),
            path.resolve(process.cwd(), 'node_modules', '.dbcube', 'bin'),
            path.resolve(__dirname, '..', 'bin'),
        ];

        // Use the first one that can be created or already exists
        for (const dir of possibleDirs) {
            try {
                if (!fs.existsSync(dir)) {
                    fs.mkdirSync(dir, { recursive: true });
                }
                // Test write permissions
                const testFile = path.join(dir, '.test');
                fs.writeFileSync(testFile, 'test');
                fs.unlinkSync(testFile);
                return dir;
            } catch {
                // Try next directory
                continue;
            }
        }

        // Fallback to .dbcube directory in temp
        const tempDir = path.join(os.tmpdir(), '.dbcube', 'bin');
        fs.mkdirSync(tempDir, { recursive: true });
        return tempDir;
    }
}

export { Downloader };